import { useMemo, useState } from 'react';

import { Accessibility, Bus, Download, Plus, Shield, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { IrregularidadeDaDocumentacao } from '@/modulos/operacao/cadastros/IrregularidadeDaDocumentacao';
import { SEM_PERMISSOES } from '@/modulos/operacao/cadastros/leitura';
import { UltimaSincronizacaoCell } from '@/modulos/operacao/comum/UltimaSincronizacaoCell';
import { OPCOES_DE_SITUACAO_DA_PESSOA, situacaoComoSelo } from '@/modulos/operacao/comum/contrato';
import { useRecursosAtivosEmOs } from '@/modulos/operacao/comum/dados';
import { STATUS_TONE } from '@/nucleo/statusBadgeTokens';
import { useUrlFilters } from '@/nucleo/ganchos/useUrlFilters';
import { OPCOES_DE_VENCIMENTO, type PresetDeVencimento } from '@/nucleo/vencimento';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { FilterPresets, type FilterPreset } from '@/ui/FilterPresets';
import { PageHeader } from '@/ui/PageHeader';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { StatusBadge } from '@/ui/StatusBadge';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { useTableViewPreferences } from '@/ui/table-view/useTableViewPreferences';
import type { TableViewColumn } from '@/ui/table-view/tabelaVisoes';

import { TipoVeiculoBadge } from './TipoVeiculoBadge';
import { VeiculoChaveVisual } from './VeiculoChaveVisual';
import { VeiculoFormulario } from './VeiculoFormulario';
import { OPCOES_DE_TIPO_DE_PESSOA, classificarTipoDeVeiculo, faltamAncoragens } from './contrato';
import { useBaixarVeiculosCsv, useTiposDeVeiculo, useVeiculos } from './dados';
import type { FiltroDeVeiculos, Veiculo } from './contrato';

/**
 * `/operacao/veiculos` — a frota do transporte escolar.
 *
 * A LINHA CARREGA TRES AVISOS DIFERENTES, e cada um responde a uma pergunta que a tabela
 * nao responderia de relance:
 *
 * 1. "LICENÇA PENDENTE" quando ha placa e nao ha CRM. O veiculo existe no cadastro e ainda
 *    nao pode rodar no programa.
 * 2. "PENDÊNCIA" quando o veiculo foi inativado por regra, e nao a mao. O motivo inteiro
 *    fica no detalhe; aqui basta dizer que ha algo a olhar.
 * 3. "ANCORAGENS" quando o veiculo se declara acessivel e nao informa quantas posicoes para
 *    cadeira de rodas tem. Sem esse numero nao se vincula o veiculo a uma rota com estudante
 *    em cadeira de rodas — e um cadastro incompleto com consequencia operacional direta.
 *
 * LE DA API (`GET bff/v1/veiculos`), sob o recorte do banco: a diretoria ve so a frota de
 * credenciado da propria DRE (0012). A placa vem inteira, como na grade da OS; o acesso ao
 * detalhe e a exportacao ficam registrados. A validade dos documentos e a aprovada na J-05:
 * por isso nao ha mais filtro de vistoria (a J-05 nao tem documento de vistoria no catalogo
 * do edital), e o de seguro olha a apolice aprovada (`APOLICE_SEGURO`). Sem leitura da
 * documentacao, coluna de irregularidade e filtro de seguro nao aparecem; sem leitura de OS,
 * o "Ativo em OS" tambem nao (regra 7 de interface).
 *
 * O FILTRO DE SEGURADORA FOI RETIRADO, A COLUNA FICOU. Decisao registrada do DTP na tela de
 * origem: filtrar frota por seguradora nao e pergunta que a operacao faz, mas saber quem
 * segura o veiculo que se esta olhando, sim.
 *
 * "NOVO VEICULO" ABRE O FORMULARIO, MAS O "SALVAR" NAO GRAVA NADA AINDA. Nenhuma tabela
 * cadastral aceita escrita hoje — nem de `ADMIN` (docs/ALVOS.md, Onda 2) —, e nao ha rota
 * de criacao na API. O dono do repositorio decidiu, em 2026-08-20, que a interface pode
 * ficar pronta antes do backend: o botao e o formulario existem, e o "Salvar" mostra um
 * aviso honesto em vez de fingir sucesso (`comum/cadastroIndisponivel.ts`).
 *
 * O CLIQUE NA LINHA ABRE O DETALHE (`/operacao/veiculos/:slug`, `CadastroDetalhePage`).
 */
export function VeiculosPage() {
  const navigate = useNavigate();

  const padroes = useMemo(
    () => ({
      search: '',
      status: '',
      tipoPessoa: '',
      tipoVeiculo: '',
      seguro: '',
      ativoOs: '',
      ancoragens: '',
    }),
    [],
  );
  const { values, setFilter, setMany, clearAll, page, setPage, perPage, setPerPage } =
    useUrlFilters(padroes);

  const filtro = useMemo<FiltroDeVeiculos>(
    () => ({
      pagina: page,
      tamanho: perPage,
      busca: values.search || undefined,
      situacao: values.status || undefined,
      tipoPessoa: values.tipoPessoa || undefined,
      tipoVeiculo: values.tipoVeiculo || undefined,
      seguro: (values.seguro || undefined) as PresetDeVencimento | undefined,
      ancoragensPendentes: values.ancoragens === 'pendente' ? true : undefined,
      ativoEmOs: values.ativoOs === 'sim' ? true : undefined,
    }),
    [
      page,
      perPage,
      values.search,
      values.status,
      values.tipoPessoa,
      values.tipoVeiculo,
      values.seguro,
      values.ancoragens,
      values.ativoOs,
    ],
  );

  const { data, isLoading, isError, error, refetch } = useVeiculos(filtro);
  const permissoes = data?.permissoes ?? SEM_PERMISSOES;
  const { data: tiposDeVeiculo } = useTiposDeVeiculo();

  // O filtro "Ativo em OS" roda no servidor; o selo "Em OS" da linha vem de
  // `/ordens-de-servico/recursos-ativos`. Ver o comentário equivalente em CondutoresPage.
  const { data: recursosAtivos } = useRecursosAtivosEmOs(permissoes.ordensDeServico);

  const linhas = useMemo(() => data?.itens ?? [], [data?.itens]);

  const temFiltro = Boolean(
    values.search ||
    values.status ||
    values.tipoPessoa ||
    values.tipoVeiculo ||
    values.seguro ||
    values.ativoOs ||
    values.ancoragens,
  );

  /**
   * `Array.isArray` e não `?? []`: a segunda forma cobre nulo e não cobre resposta com
   * outra forma — e `.map` numa dessas estoura no meio do render da listagem inteira, por
   * causa de um combo de filtro. Mesma cautela de `useOpcoesDeDre`.
   */
  const tipos = Array.isArray(tiposDeVeiculo) ? tiposDeVeiculo : undefined;

  const opcoesDeTipoDeVeiculo = useMemo(
    () => (tipos ?? []).map((tipo) => ({ value: tipo.slug, label: tipo.nome })),
    [tipos],
  );

  const campos: FilterFieldConfig[] = [
    {
      key: 'search',
      label: 'Busca',
      type: 'search',
      placeholder: 'Buscar por placa, CRM, marca ou modelo…',
      ariaLabel: 'Buscar veículos',
      flex: true,
    },
    {
      key: 'tipoVeiculo',
      label: 'Tipo',
      type: 'select',
      options: opcoesDeTipoDeVeiculo,
      width: 'w-full sm:w-[160px]',
    },
    {
      key: 'tipoPessoa',
      label: 'Pessoa',
      type: 'select',
      options: [...OPCOES_DE_TIPO_DE_PESSOA],
      width: 'w-full sm:w-[150px]',
    },
    ...(permissoes.ordensDeServico
      ? [
          {
            key: 'ativoOs',
            label: 'Ativo em OS',
            type: 'select' as const,
            options: [{ value: 'sim', label: 'Apenas ativos em OS' }],
            width: 'w-full sm:w-[170px]',
          },
        ]
      : []),
    {
      key: 'ancoragens',
      label: 'Acessibilidade',
      type: 'select',
      options: [{ value: 'pendente', label: 'Acessíveis sem ancoragens' }],
      placeholder: 'Acessibilidade',
      width: 'w-full sm:w-[220px]',
    },
    ...(permissoes.documentacao
      ? [
          {
            key: 'seguro',
            label: 'Seguro',
            type: 'select' as const,
            options: [...OPCOES_DE_VENCIMENTO],
            placeholder: 'Vencimento',
            width: 'w-full sm:w-[170px]',
          },
        ]
      : []),
    {
      key: 'status',
      label: 'Situação',
      type: 'select',
      options: [...OPCOES_DE_SITUACAO_DA_PESSOA],
      width: 'w-full sm:w-[140px]',
    },
  ];

  const atalhos: FilterPreset[] = [
    ...(permissoes.documentacao
      ? [
          {
            id: 'seguro-30',
            label: 'Seguro vence em 30 dias',
            values: { seguro: '30dias' },
            description: 'Veículos com a apólice aprovada vencendo nos próximos 30 dias',
          },
        ]
      : []),
    {
      id: 'ancoragens',
      label: 'Acessíveis sem ancoragens',
      values: { ancoragens: 'pendente' },
      description: 'Veículos declarados acessíveis sem o número de ancoragens informado',
    },
    { id: 'inativos', label: 'Inativos', values: { status: 'INATIVO' } },
  ];

  const colunas = useMemo<TableViewColumn<Veiculo>[]>(
    () => [
      {
        id: 'placa',
        header: 'CRM / Placa',
        cell: (veiculo) => (
          <div className="flex flex-wrap items-center gap-2">
            <VeiculoChaveVisual crm={veiculo.crm} placa={veiculo.placa} tamanho="sm" />

            {recursosAtivos?.veiculos.has(veiculo.id) && (
              <Badge variant="outline" className="h-5 px-1.5 text-[10px]">
                Em OS
              </Badge>
            )}

            {veiculo.motivo_inativacao && (
              <Badge
                variant="outline"
                className={`h-5 px-1.5 text-[10px] ${STATUS_TONE.warning}`}
                title={`Inativado por regra${
                  veiculo.tag_origem_inativacao ? ` (${veiculo.tag_origem_inativacao})` : ''
                }. Abra o veículo para ver o motivo.`}
              >
                Pendência
              </Badge>
            )}

            {faltamAncoragens(veiculo) && (
              <Badge
                variant="outline"
                className={`h-5 gap-1 px-1.5 text-[10px] ${STATUS_TONE.warning}`}
                title="Veículo acessível sem o número de ancoragens informado. Sem esse número não se vincula o veículo a rota com estudante em cadeira de rodas."
              >
                <Accessibility className="h-3 w-3" aria-hidden="true" />
                Ancoragens
              </Badge>
            )}
          </div>
        ),
        isCritical: true,
        canHide: false,
        tooltip:
          'CRM é a licença municipal do veículo — o documento do TEG. A placa é do Detran e muda quando o veículo é substituído.',
      },
      {
        id: 'marca_modelo',
        header: 'Marca/Modelo',
        cell: (veiculo) => (
          <span className="text-sm">
            {[veiculo.marca, veiculo.modelo].filter(Boolean).join(' ') || '—'}
          </span>
        ),
        sortKey: (veiculo) => `${veiculo.marca ?? ''} ${veiculo.modelo ?? ''}`.trim(),
      },
      {
        id: 'tipo_veiculo',
        header: 'Tipo',
        cell: (veiculo) => <TipoVeiculoBadge veiculo={veiculo} tipos={tipos} />,
        sortKey: (veiculo) => classificarTipoDeVeiculo(veiculo, tipos)?.ordem ?? 999,
        linkedFilterKeys: ['tipoVeiculo'],
      },
      {
        id: 'pessoa',
        header: 'Pessoa',
        // Cooperativa ganha selo próprio ainda que seja PJ para toda regra legal: o rateio
        // entre cooperados muda o demonstrativo, e quem opera precisa distinguir de relance.
        cell: (veiculo) => {
          const tipo = veiculo.credenciados?.tipo;
          if (!tipo) {
            return <span className="text-muted-foreground text-xs">—</span>;
          }
          if (tipo === 'COOPERATIVA') {
            return (
              <Badge variant="secondary" className="gap-1">
                <Users className="h-3 w-3" aria-hidden="true" />
                Cooperativa
              </Badge>
            );
          }
          return (
            <Badge variant="outline" className="text-xs">
              {tipo}
            </Badge>
          );
        },
        linkedFilterKeys: ['tipoPessoa'],
      },
      {
        id: 'seguradora',
        header: 'Seguradora',
        cell: (veiculo) => {
          const nome = veiculo.seguro_seguradora;
          if (!nome) {
            return <span className="text-muted-foreground text-xs">—</span>;
          }
          return (
            <span className="inline-flex items-center gap-1 text-sm">
              <Shield className="text-muted-foreground h-3 w-3" aria-hidden="true" />
              {nome}
            </span>
          );
        },
        sortKey: (veiculo) => veiculo.seguro_seguradora ?? '',
        tooltip: 'Seguradora informada no cadastro do veículo.',
      },
      ...(permissoes.documentacao
        ? [
            {
              id: 'irregularidade',
              header: 'Irregularidade',
              cell: (veiculo: Veiculo) => (
                <IrregularidadeDaDocumentacao
                  validades={veiculo.validades ?? []}
                  situacao={veiculo.status}
                  semVinculo={!veiculo.credenciado_id}
                />
              ),
              tooltip:
                'Validade aprovada de cada documento em vigor, na análise do DTP, mais a situação e o vínculo com credenciado.',
            },
          ]
        : []),
      {
        id: 'ultima_sincronizacao',
        header: 'Última sincronização',
        cell: (veiculo) => (
          <UltimaSincronizacaoCell
            origem={veiculo.origem_dado}
            sincronizadoEm={veiculo.sincronizado_em}
            emContingencia={veiculo.em_contingencia}
          />
        ),
        sortKey: 'sincronizado_em',
      },
      {
        id: 'status',
        header: 'Situação',
        cell: (veiculo) => (
          <StatusBadge status={situacaoComoSelo(veiculo.status)} label={veiculo.status} />
        ),
        sortKey: 'status',
        linkedFilterKeys: ['status'],
        isCritical: true,
        canHide: false,
        isStatusColumn: true,
      },
    ],
    [tipos, permissoes.documentacao, recursosAtivos],
  );

  const tableView = useTableViewPreferences({
    contextKey: 'veiculos:list',
    columns: colunas,
    filterValues: values,
  });

  const baixarCsv = useBaixarVeiculosCsv();
  const [baixando, setBaixando] = useState(false);
  const [novoAberto, setNovoAberto] = useState(false);

  const exportar = async () => {
    setBaixando(true);
    try {
      const quantidade = await baixarCsv(filtro);
      toast('Planilha gerada', {
        description: `${quantidade} veículo(s) no arquivo, com os filtros que você está usando.`,
      });
    } catch {
      toast.error('Não foi possível gerar o arquivo', {
        description: 'Tente novamente em instantes.',
      });
    } finally {
      setBaixando(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Veículos"
        description="A frota do transporte escolar, com licença, vistoria, seguro e acessibilidade."
      >
        <Button
          variant="outline"
          onClick={() => void exportar()}
          disabled={baixando || isLoading}
          aria-label="Exportar veículos em CSV"
        >
          <Download className="h-4 w-4 sm:mr-2" aria-hidden="true" />
          <span className="hidden sm:inline">{baixando ? 'Gerando…' : 'Exportar CSV'}</span>
        </Button>
        <Button onClick={() => setNovoAberto(true)}>
          <Plus className="h-4 w-4 sm:mr-2" aria-hidden="true" />
          <span className="hidden sm:inline">Novo Veículo</span>
        </Button>
      </PageHeader>

      <ResponsiveModal
        open={novoAberto}
        onOpenChange={setNovoAberto}
        title={
          <>
            <Bus className="h-5 w-5 text-primary" aria-hidden="true" />
            Novo Veículo
          </>
        }
        description="Cadastre um novo veículo na frota do transporte escolar."
        desktopMaxWidth="max-w-4xl"
      >
        <VeiculoFormulario onCancelar={() => setNovoAberto(false)} />
      </ResponsiveModal>

      <FilterPresets
        presets={atalhos}
        currentValues={values}
        onApply={(valores) => {
          clearAll();
          setMany(valores);
        }}
      />

      <FilterBar
        fields={campos}
        values={values}
        onChange={setFilter}
        onClear={clearAll}
        defaultValues={padroes}
      />

      {isError ? (
        <ErrorState
          variant="load-failed"
          description={
            error?.aviso ??
            'Não foi possível buscar a frota agora. Verifique sua conexão e tente novamente.'
          }
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      ) : (
        <DataTable
          columns={tableView.orderedColumns as DataTableColumn<Veiculo>[]}
          personalizacao={tableView}
          data={linhas}
          onRowClick={(veiculo) => navigate(`/operacao/veiculos/${veiculo.slug}`)}
          isLoading={isLoading}
          total={data?.total}
          page={page}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
          emptyIcon={Bus}
          emptyTitle="Nenhum veículo encontrado"
          emptyDescription={
            temFiltro
              ? 'Nenhum veículo atende a esses filtros. Tente afrouxar a busca.'
              : 'Quando houver veículo cadastrado na frota, ele aparece aqui.'
          }
          minWidth="900px"
          ariaLabel="Lista de veículos"
          skeletonColumns={7}
        />
      )}
    </div>
  );
}

export default VeiculosPage;
